import React, { useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment, Html } from '@react-three/drei';

// Placeholder 3D objects until you add your GLB model
const FloatingBox = ({ position }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += 0.01;
      meshRef.current.rotation.y += 0.01;
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime) * 0.2;
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={position}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      scale={hovered ? 1.2 : 1}
    >
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial 
        color={hovered ? '#00ff88' : '#0088ff'} 
        metalness={0.6}
        roughness={0.2}
      />
      {hovered && (
        <Html distanceFactor={10}>
          <div className="bg-black bg-opacity-80 text-white px-3 py-2 rounded text-sm whitespace-nowrap">
            Click to interact
          </div>
        </Html>
      )}
    </mesh>
  );
};

const FloatingSphere = ({ position }) => {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.position.y = position[1] + Math.cos(state.clock.elapsedTime * 0.8) * 0.3;
      meshRef.current.rotation.z += 0.005;
    }
  });

  return (
    <mesh ref={meshRef} position={position}>
      <sphereGeometry args={[0.8, 32, 32]} />
      <meshStandardMaterial 
        color="#ff0088" 
        metalness={0.8}
        roughness={0.1}
        emissive="#ff0088"
        emissiveIntensity={0.2}
      />
    </mesh>
  );
};

const Ground = () => {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
      <planeGeometry args={[50, 50]} />
      <meshStandardMaterial 
        color="#111111" 
        metalness={0.1}
        roughness={0.8}
      />
    </mesh>
  );
};

const Scene3D = () => {
  return (
    <div className="w-full h-screen bg-black">
      <Canvas shadows>
        <PerspectiveCamera makeDefault position={[0, 2, 8]} fov={75} />
        
        {/* Lighting */}
        <ambientLight intensity={0.3} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={1} 
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <pointLight position={[-10, 5, -10]} intensity={0.5} color="#0088ff" />
        <pointLight position={[10, 5, 10]} intensity={0.5} color="#ff0088" />

        {/* Environment for reflections */}
        <Environment preset="city" />

        {/* 3D Objects - Placeholder until you add GLB model */}
        <FloatingBox position={[-2, 1, 0]} />
        <FloatingBox position={[2, 1, 0]} />
        <FloatingSphere position={[0, 2, -2]} />
        
        {/* Ground */}
        <Ground />

        {/* Camera Controls */}
        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={3}
          maxDistance={20}
          maxPolarAngle={Math.PI / 2}
        />

        {/* Grid Helper for reference */}
        <gridHelper args={[50, 50, '#333333', '#1a1a1a']} position={[0, -2, 0]} />
      </Canvas>

      {/* UI Overlay */}
      <div className="absolute top-8 left-8 text-white space-y-2">
        <h2 className="text-2xl font-bold tracking-wide">3D EXPERIENCE</h2>
        <p className="text-sm tracking-wider opacity-70">DRAG TO ROTATE • SCROLL TO ZOOM</p>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-8 left-8 text-white text-sm tracking-wider opacity-70">
        <p>PLACEHOLDER SCENE - ADD YOUR GLB MODEL TO /public/models/</p>
      </div>
    </div>
  );
};

export default Scene3D;
